import { useCallback, useEffect, useRef } from "react";
import { MenuDataType } from "~/components/shared-components/mobile-menu/type";

interface ImageUrls {
  small: string;
  medium: string;
  large: string;
  hd: string;
  original: string;
}

interface PreloadedImage {
  urls: ImageUrls;
  status: "loading" | "loaded" | "error";
}

// Global cache to store preloaded images across component instances
const imageCache = new Map<string, PreloadedImage>();

/**
 * Custom hook to preload images from menu data
 * Efficiently preloads images that are likely to be shown on hover
 */
export const useImagePreloader = (categories: MenuDataType[]) => {
  const preloadedImages = useRef(new Set<string>());

  const preloadImage = useCallback((urls: ImageUrls): Promise<void> => {
    const cacheKey = urls.small; // Use small URL as cache key

    if (imageCache.has(cacheKey)) {
      return Promise.resolve();
    }

    // Set loading state
    imageCache.set(cacheKey, { urls, status: "loading" });

    return new Promise((resolve, reject) => {
      const img = new Image();

      // Preload the medium size image for desktop menus
      img.src = urls.medium || urls.small;

      img.onload = () => {
        imageCache.set(cacheKey, { urls, status: "loaded" });
        preloadedImages.current.add(cacheKey);
        resolve();
      };

      img.onerror = () => {
        imageCache.set(cacheKey, { urls, status: "error" });
        reject(new Error(`Failed to preload image: ${img.src}`));
      };
    });
  }, []);

  const extractImageUrls = useCallback((categories: MenuDataType[]): ImageUrls[] => {
    const imageUrls: ImageUrls[] = [];

    categories.forEach((category) => {
      category.menu_group?.forEach((group) => {
        // Only preload images for components that actually use images
        if (["icon", "list", "banner"].includes(group.disp_type)) {
          group.menu_item?.forEach((item) => {
            if (item.img) {
              if (typeof item.img === "string") {
                imageUrls.push({
                  small: item.img,
                  medium: item.img,
                  large: item.img,
                  hd: item.img,
                  original: item.img,
                });
              } else {
                imageUrls.push({
                  small: item.img.s?.url || "",
                  medium: item.img.m?.url || "",
                  large: item.img.l?.url || "",
                  hd: item.img.hd?.url || "",
                  original: item.img.og?.url || "",
                });
              }
            }
          });
        }
      });
    });

    return imageUrls;
  }, []);

  const preloadImagesWithThrottle = useCallback(
    async (imageUrls: ImageUrls[]) => {
      const BATCH_SIZE = 3; // Load 3 images at a time to avoid overwhelming the browser
      const DELAY_BETWEEN_BATCHES = 1; // 100ms delay between batches

      for (let i = 0; i < imageUrls.length; i += BATCH_SIZE) {
        const batch = imageUrls.slice(i, i + BATCH_SIZE);

        try {
          await Promise.allSettled(batch.map((urls) => preloadImage(urls)));
        } catch (error) {
          console.warn("Batch preload failed:", error);
        }

        // Add small delay between batches to prevent blocking the main thread
        if (i + BATCH_SIZE < imageUrls.length) {
          await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }
    },
    [preloadImage],
  );

  useEffect(() => {
    if (!categories || categories.length === 0) return;
    let timeout: NodeJS.Timeout;

    // Use requestIdleCallback when available for better performance
    const schedulePreload = () => {
      const imageUrls = extractImageUrls(categories);

      if (imageUrls.length === 0) return;

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(
          () => {
            preloadImagesWithThrottle(imageUrls);
          },
          { timeout: 5000 },
        );
      } else {
        // Fallback for browsers without requestIdleCallback
        timeout = setTimeout(() => {
          preloadImagesWithThrottle(imageUrls);
        }, 100);
      }
    };

    schedulePreload();

    return () => clearTimeout(timeout);
  }, [categories, extractImageUrls, preloadImagesWithThrottle]);

  // Function to check if an image is preloaded
  const isImagePreloaded = useCallback((url: string): boolean => {
    const cached = imageCache.get(url);
    return cached?.status === "loaded";
  }, []);

  // Function to get preloaded image status
  const getImageStatus = useCallback((url: string): "loading" | "loaded" | "error" | "not-started" => {
    const cached = imageCache.get(url);
    return cached?.status || "not-started";
  }, []);

  return {
    isImagePreloaded,
    getImageStatus,
    preloadedCount: preloadedImages.current.size,
  };
};

export default useImagePreloader;
